import React from "react";
import { STATUS, money } from "@/components/obboard/BoardFilters";
import { fmtCompact } from "@/lib/format";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const sumAll = (list) => list.reduce((a, m) => { Object.entries(m).forEach(([c, v]) => { a[c] = (a[c] || 0) + v; }); return a; }, {});
const th = "px-2 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-medium";

const Cell = ({ c, cur, onClick, testid }) => (
  <td className="px-1 py-1">
    {c.status === "none" ? <div className="h-9" /> : (
      <button type="button" data-testid={testid} onClick={onClick} className={`w-full h-9 rounded-sm border text-[11px] mono leading-tight ${STATUS[c.status].cls} hover:brightness-95`}>
        {c.amount != null ? fmtCompact(c.amount, cur) : "✓"}{c.count > 1 && <span className="block text-[9px] opacity-70">×{c.count}</span>}
      </button>
    )}
  </td>
);

const TotalRow = ({ label, totals, k, tone, testid }) => (
  <tr className="border-t border-slate-200 bg-slate-50/60" data-testid={testid}>
    <td className={`sticky left-0 bg-slate-50 px-3 py-2 text-xs font-semibold ${tone}`}>{label}</td>
    {totals.map((t, i) => <td key={i} className={`px-1 py-2 text-center text-[11px] mono ${tone}`}>{money(t[k], true)}</td>)}
    <td className={`px-2 py-2 text-right text-xs mono font-semibold ${tone}`}>{money(sumAll(totals.map((t) => t[k])), true)}</td>
    <td />
  </tr>
);

export const YearGrid = ({ data, onMonth }) => (
  <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="ob-year-grid">
    <table className="w-full min-w-[1200px] text-sm">
      <thead className="bg-slate-50 border-b border-slate-200"><tr>
        <th className={`${th} text-left sticky left-0 bg-slate-50 min-w-[240px]`}>Obligation</th>
        {MONTHS.map((m, i) => <th key={m} className={`${th} text-center w-[72px]`}><button type="button" data-testid={`ob-year-month-${i + 1}`} onClick={() => onMonth(i + 1)} className="hover:text-blue-700">{m}</button></th>)}
        <th className={`${th} text-right`}>Total due</th><th className={`${th} text-right pr-3`}>Paid</th>
      </tr></thead>
      <tbody>
        {data.rows.map((r) => (
          <tr key={r.obligation_id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`ob-year-row-${r.obligation_id}`}>
            <td className="sticky left-0 bg-white px-3 py-1.5">
              <div className="text-sm text-slate-900 truncate max-w-[260px]">{r.name}</div>
              <div className="text-[11px] text-slate-500 truncate max-w-[260px]">{[r.category, r.entity, r.payment_mode === "auto" ? "Auto debit" : null].filter(Boolean).join(" · ")}</div>
            </td>
            {r.cells.map((c, i) => <Cell key={i} c={c} cur={r.currency} testid={`ob-year-cell-${r.obligation_id}-${i + 1}`} onClick={() => onMonth(i + 1)} />)}
            <td className="px-2 text-right mono text-xs text-slate-900">{money(r.total.due, true)}</td>
            <td className="px-2 pr-3 text-right mono text-xs text-emerald-700">{money(r.total.paid, true)}</td>
          </tr>
        ))}
        {data.rows.length === 0 && <tr><td colSpan={15} className="py-10 text-center text-slate-500" data-testid="ob-year-empty">No obligations fall in this year for these filters.</td></tr>}
      </tbody>
      {data.rows.length > 0 && (
        <tfoot>
          <TotalRow label="Total due" totals={data.totals} k="due" tone="text-slate-900" testid="ob-year-total-due" />
          <TotalRow label="Paid" totals={data.totals} k="paid" tone="text-emerald-700" testid="ob-year-total-paid" />
          <TotalRow label="Still to pay (incl. overdue)" totals={data.totals} k="unpaid" tone="text-blue-800" testid="ob-year-total-unpaid" />
          <tr className="border-t-2 border-blue-200" data-testid="ob-year-balance">
            <td colSpan={16} className="sticky left-0 bg-white px-3 pt-4 pb-1">
              <div className="text-sm font-semibold text-slate-900">Bank balance required by entity</div>
              <div className="text-[11px] text-slate-500">Unpaid amounts due each month (upcoming + overdue)</div>
            </td>
          </tr>
          {data.balance_required.map((b) => (
            <tr key={b.entity} className="border-t border-slate-100" data-testid={`ob-year-balance-${b.entity}`}>
              <td className="sticky left-0 bg-white px-3 py-2 text-sm text-slate-800">{b.entity === "—" ? "No entity" : b.entity}</td>
              {b.months.map((m, i) => <td key={i} className="px-1 py-2 text-center text-[11px] mono text-blue-800">{money(m, true)}</td>)}
              <td className="px-2 py-2 text-right mono text-xs font-semibold text-blue-800">{money(sumAll(b.months), true)}</td>
              <td />
            </tr>
          ))}
          {data.balance_required.length === 0 && <tr><td colSpan={16} className="px-3 py-3 text-xs text-slate-500">Nothing left to pay this year.</td></tr>}
        </tfoot>
      )}
    </table>
  </div>
);
